import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { getDataList } from '../../../api/common';

/**
 * ModalResolverItems - Modal to manually match items not found in Excel upload
 * @param {Object} props - Component props
 * @param {boolean} props.show - Modal visibility state
 * @param {Function} props.onHide - Handler to close modal
 * @param {Array} props.equiposSinItem - Array of equipos with unresolved items
 * @param {Function} props.onResolve - Handler receiving resolved equipos array
 * @returns {JSX.Element}
 */
const ModalResolverItems = ({ show, onHide, equiposSinItem = [], onResolve }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selections, setSelections] = useState({});

    // Load all items from API
    useEffect(() => {
        if (show) {
            loadItems();
        }
    }, [show]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDataList('Items');
            setItems(response.data.data || []);
        } catch (err) {
            setError('Error al cargar items: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // Format options for react-select
    const itemOptions = items.map(item => ({
        value: item._id,
        label: item.nombre,
        data: item
    }));

    const handleSelectItem = (equipoIndex, option) => {
        setSelections(prev => ({
            ...prev,
            [equipoIndex]: option?.data || null
        }));
    };

    const handleResolve = () => {
        // Create resolved equipos array with selected items
        const resolved = equiposSinItem.map((equipo, index) => ({
            ...equipo,
            item: selections[index]?._id || null
        }));
        
        onResolve(resolved);
        setSelections({});
        onHide();
    };

    const handleClose = () => {
        setSelections({});
        onHide();
    };

    // Check if all rows are resolved
    const allResolved = equiposSinItem.every((_, index) => selections[index]);

    return (
        <Modal show={show} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Resolver Items No Encontrados</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Cargando items...</p>
                    </div>
                ) : (
                    <>
                        <p className="mb-3">
                            Los siguientes equipos tienen nombres de item que no coinciden exactamente. 
                            Por favor, seleccione el item correcto para cada uno:
                        </p>

                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>Item Buscado</th>
                                    <th>Serie</th>
                                    <th>Marca</th>
                                    <th>Modelo</th>
                                    <th style={{ width: '30%' }}>Seleccionar Item Correcto *</th>
                                </tr>
                            </thead>
                            <tbody>
                                {equiposSinItem.map((equipo, index) => (
                                    <tr key={index}>
                                        <td>
                                            <span className="text-danger fw-bold">
                                                {equipo.itemNombreBuscado || 'N/A'}
                                            </span>
                                        </td>
                                        <td>{equipo.serie}</td>
                                        <td>{equipo.marca || 'N/A'}</td>
                                        <td>{equipo.modelo || 'N/A'}</td>
                                        <td>
                                            <Select
                                                options={itemOptions}
                                                value={selections[index] ? {
                                                    value: selections[index]._id,
                                                    label: selections[index].nombre
                                                } : null}
                                                onChange={(option) => handleSelectItem(index, option)}
                                                placeholder="Seleccione item..."
                                                isClearable
                                                noOptionsMessage={() => "No hay items disponibles"}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleResolve}
                    disabled={!allResolved || loading}
                >
                    Confirmar ({Object.keys(selections).length}/{equiposSinItem.length} resueltos)
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalResolverItems;
